import { useEffect, useState } from 'react';
import { Eye, EyeOff, LogOut, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMd, type DashboardPayload } from '@gc/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import { AccountMenuLink } from '@/features/account/AccountMenuLink';
import { ClickSoundToggle } from './ClickSoundToggle';
import { LanguageSwitcher } from './LanguageSwitcher';

interface TopBarProps {
  onToggleSidebar: () => void;
}

/**
 * The shell's pulse (approved board card, top strip): the business day in
 * progress, how many machines are earning right now, and what today has taken.
 * One quiet request a minute — the dashboard's own 30s poll stays the fast
 * copy; this one only has to be fresh enough for a glance from any screen.
 */
const PULSE_INTERVAL_MS = 60_000;

function useShellPulse(enabled: boolean): DashboardPayload | null {
  const [payload, setPayload] = useState<DashboardPayload | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await api.get<DashboardPayload>('/dashboard?period=DAY');
        if (!cancelled) setPayload(response);
      } catch {
        // The topbar is chrome, not a report: a failed pulse keeps the last
        // figures rather than raising an error over whatever screen is open.
      }
    };

    void load();
    const timer = setInterval(() => void load(), PULSE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled]);

  return payload;
}

export function TopBar({ onToggleSidebar }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const { employee, venue, can, signOut } = useAuth();

  // Today's takings are money — the pulse shows only to eyes already allowed
  // to read the dashboard, and never fetches for anyone else.
  const canSeePulse = can('dashboard.view');
  const pulse = useShellPulse(canSeePulse);

  /** Masked until pressed, and masked again on every reload. See the button. */
  const [takingsShown, setTakingsShown] = useState(false);

  return (
    <header
      // Translucent ink over the blueprint grid, per the approved shell card —
      // the chrome floats on the schematic rather than sitting on a slab.
      className="flex items-center gap-3 border-b border-subtle bg-raised/80 px-3 backdrop-blur-md"
      style={{ height: 'var(--topbar-height)' }}
    >
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Toggle navigation"
        className="rounded-control p-2 text-content-secondary hover:bg-hover hover:text-content"
      >
        <Menu className="h-5 w-5" aria-hidden />
      </button>

      {/* Venue branding comes from settings — never hard-coded. Truncated on
          phones so a long venue name cannot squeeze out the controls. */}
      <div className="flex min-w-0 items-center gap-2">
        {venue?.logoUrl ? (
          <img src={venue.logoUrl} alt="" className="h-7 w-7 shrink-0 rounded object-contain" />
        ) : null}
        <span className="truncate font-semibold tracking-wide text-content">
          {venue?.name ?? ''}
        </span>
      </div>

      {/* The business day in progress — what "today" means on every figure
          this bar shows. Desktop only; phone pixels belong to the name. */}
      {pulse ? (
        <span className="hidden text-[11px] text-content-muted lg:inline">
          {t('dashboard.businessDate')}{' '}
          <span className="tabular text-content-secondary">{pulse.businessDate}</span>
        </span>
      ) : null}

      <div className="flex-1" />

      {/* The two figures a passing glance wants (card `.kpi`): machines
          earning now, and the day's takings so far. Real payload fields. */}
      {pulse ? (
        <div className="me-1 hidden items-end gap-5 text-end md:flex">
          <div>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-content-muted">
              {t('topbar.inPlay')}
            </span>
            <span className="tabular block text-[15px] leading-tight font-semibold text-primary [text-shadow:var(--glow-primary)]">
              {pulse.occupancy.busy}
            </span>
          </div>
          {/*
            THE DAY'S TAKINGS ARE HIDDEN UNTIL ASKED FOR.
            This sits in the chrome of every screen, which means it is on show
            whenever a customer is leaning over the counter or a member of staff
            is looking at something else entirely. Owner's call: masked by
            default, one press to read it.

            Deliberately NOT remembered. It re-hides on every reload, because a
            figure that stays uncovered once opened is uncovered for the rest of
            the shift — which is the thing being avoided.
          */}
          <button
            type="button"
            data-today-takings
            onClick={() => setTakingsShown((shown) => !shown)}
            aria-pressed={takingsShown}
            title={takingsShown ? t('topbar.hideToday') : t('topbar.showToday')}
            className="rounded-control px-1 text-end transition-colors hover:bg-hover"
          >
            <span className="block text-[10px] uppercase tracking-[0.22em] text-content-muted">
              {t('topbar.today')}
            </span>
            <span className="tabular flex items-center justify-end gap-1.5 text-[15px] leading-tight font-semibold text-content">
              {takingsShown ? (
                <>
                  {formatMd(pulse.day.salesMd, {
                    currencySymbol: pulse.currencySymbol,
                    locale: i18n.language,
                  })}
                  <EyeOff className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
                </>
              ) : (
                <>
                  {/* Dots rather than a blank: the row keeps its height and the
                      figure keeps its place, so revealing it moves nothing. */}
                  <span aria-hidden>••••••</span>
                  <span className="sr-only">{t('topbar.showToday')}</span>
                  <Eye className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
                </>
              )}
            </span>
          </button>
        </div>
      ) : null}

      <ClickSoundToggle />
      <LanguageSwitcher />

      {employee ? (
        <div className="flex shrink-0 items-center gap-2">
          {/* The name is a nicety; on a phone the pixels belong to the venue
              name and the sign-out button, which must never be pushed off. */}
          <AccountMenuLink name={employee.name} />
          <button
            type="button"
            onClick={() => void signOut()}
            title={t('auth.signOut')}
            aria-label={t('auth.signOut')}
            className="rounded-control p-2 text-content-secondary hover:bg-hover hover:text-content"
          >
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
    </header>
  );
}
